<div class="flex flex-col h-full min-h-0 bg-gray-100 rounded-xl overflow-hidden border border-gray-200">

  <!-- Top bar: artboard label, zoom controls, Apply -->
  <div class="flex items-center justify-between gap-3 px-3.5 py-2 bg-[#1f2430] text-gray-200 border-b border-white/[0.08] flex-wrap shrink-0">
    <div class="flex items-center gap-2 min-w-0">
      <i class="fa fa-image text-primary-400"></i>
      <span class="text-xs font-bold uppercase tracking-wide truncate">Artboard</span>
      <span class="text-[10px] text-gray-400 hidden sm:inline">{{ artboardSize.width }}&times;{{ artboardSize.height
        }}</span>
    </div>

    <div class="zoom-controls">
      <button type="button" (click)="zoomOut()" title="Zoom out"><i class="fa fa-minus"></i></button>
      <span class="zoom-value">{{ zoomPercent }}%</span>
      <button type="button" (click)="zoomIn()" title="Zoom in"><i class="fa fa-plus"></i></button>
      <button type="button" (click)="zoomTo100()" title="Reset to 100%" class="!w-auto px-2 text-[10px]">100%</button>
      <button type="button" (click)="zoomToFit()" title="Fit to screen"><i class="fa fa-expand"></i></button>
    </div>

    @if (selectionCount() > 1) {
    <div class="zoom-controls relative">
      <button type="button" [class.active]="showAlignMenu()" (click)="toggleAlignMenu()"
        title="Align selected objects">
        <i class="fa fa-align-left"></i>
      </button>
      @if (showAlignMenu()) {
      <div class="rail-popover !left-0 !top-full !ml-0 !mt-2">
        <div class="grid grid-cols-3 gap-1 p-1">
          <button type="button" class="shape-grid-item icon-btn" data-tip="Align left"
            (click)="alignSelection('left')"><i class="fa fa-align-left"></i></button>
          <button type="button" class="shape-grid-item icon-btn" data-tip="Align center"
            (click)="alignSelection('center-h')"><i class="fa fa-align-center"></i></button>
          <button type="button" class="shape-grid-item icon-btn" data-tip="Align right"
            (click)="alignSelection('right')"><i class="fa fa-align-right"></i></button>
          <button type="button" class="shape-grid-item icon-btn" data-tip="Align top"
            (click)="alignSelection('top')"><i class="fa fa-align-left fa-rotate-90"></i></button>
          <button type="button" class="shape-grid-item icon-btn" data-tip="Align middle"
            (click)="alignSelection('middle-v')"><i class="fa fa-align-center fa-rotate-90"></i></button>
          <button type="button" class="shape-grid-item icon-btn" data-tip="Align bottom"
            (click)="alignSelection('bottom')"><i class="fa fa-align-right fa-rotate-90"></i></button>
        </div>
      </div>
      }
    </div>
    }

    <div class="zoom-controls">
      <button type="button" [class.active]="gridEnabled()" (click)="toggleGrid()"
        title="Show grid &amp; snap to it while dragging">
        <i class="fa fa-table-cells"></i>
      </button>
      <!-- Always rendered (just disabled when off) rather than @if-toggled —
           an @if here changes this group's width every time grid is
           switched on/off, which shifts every button after it in the
           topbar (a space-between-y layout reflows the whole row). -->
      <select
        class="h-[26px] leading-[26px] text-[10.5px] font-semibold text-gray-200 bg-white/[0.08] border-none rounded-md px-1.5 py-0 disabled:opacity-[0.35] disabled:cursor-default"
        [disabled]="!gridEnabled()" [ngModel]="gridSize()" (ngModelChange)="setGridSize(+$event)">
        @for (size of gridSizeChoices; track size) {
        <option [value]="size">{{ size }}px</option>
        }
      </select>
    </div>

    <div class="flex items-center gap-2">
      @if (isDirty()) {
      <button type="button" (click)="onCancelEdits()" [disabled]="isSaving() || isBusy()"
        class="text-xs font-semibold px-3 py-1.5 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white disabled:opacity-50 transition-all">
        Cancel
      </button>
      }
      <button type="button" (click)="exportImage()" [disabled]="isBusy() || isSaving()" title="Export as image"
        class="export-btn">
        <i class="fa fa-download"></i>
      </button>
      <button type="button" (click)="onSave()" [disabled]="isSaving()" [class.is-dirty]="isDirty() && !isSaving()"
        class="save-btn text-xs font-semibold px-3.5 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white shadow-2xs transition-all flex items-center gap-1.5">
        @if (isSaving()) {
        <i class="fa fa-spinner fa-spin"></i>
        } @else {
        <i class="fa fa-check"></i>
        }
        {{ isSaving() ? 'Saving...' : 'Save' }}
      </button>
    </div>
  </div>

  @if (editorError(); as error) {
  <div
    class="flex items-center gap-2.5 px-3.5 py-2 bg-[#3b1d1d] text-red-200 text-xs border-b border-white/[0.08] shrink-0">
    <i class="fa fa-triangle-exclamation text-red-400 shrink-0"></i>
    <span class="flex-1 min-w-0">{{ error }}</span>
    <button type="button" (click)="editorError.set(null)" title="Dismiss"
      class="shrink-0 text-red-200 w-[22px] h-[22px] flex items-center justify-center rounded-md hover:bg-white/10 hover:text-white">
      <i class="fa fa-xmark"></i>
    </button>
  </div>
  }

  <div class="flex flex-1 min-h-0">

    <!-- LEFT TOOL RAIL — add-on layer tools -->
    <div class="tool-rail">
      <div class="rail-item">
        <button type="button" class="rail-btn" [class.active]="showFileMenu()" (click)="toggleFileMenu()"
          title="Import or export a layered image file">
          <i class="fa fa-file"></i><span>File</span>
        </button>

        @if (showFileMenu()) {
        <div class="rail-popover">
          <button type="button" class="rail-popover-item" (click)="triggerImportTemplate()">
            <i class="fa fa-file-import w-4 text-center"></i> Import
          </button>
          <button type="button" class="rail-popover-item" (click)="exportTemplate()">
            <i class="fa fa-file-export w-4 text-center"></i> Export
          </button>
        </div>
        }
      </div>

      @if (isAllowed('photo') || isAllowed('background-image') || isAllowed('logo')) {
      <!-- Each flyout-bearing tool gets its own relatively-positioned wrapper
           so its popover is anchored exactly to that button (top:0/left:100%
           of the wrapper) without any hardcoded pixel offsets — and, crucially,
           the wrapper itself carries no overflow clipping, so the flyout is
           never cut off the way it would be if it were positioned relative to
           .tool-rail (which needs overflow-y:auto for small viewports). -->
      <div class="rail-item">
        <button type="button" class="rail-btn" [class.active]="showPhotoPicker()" (click)="togglePhotoPicker()"
          title="Add a photo">
          <i class="fa fa-image"></i><span>Photo</span>
        </button>

        <!-- Photo popover: From device / From albums -->
        @if (showPhotoPicker()) {
        <div class="rail-popover" [class.wide]="photoPickerView() !== 'menu'">
          @switch (photoPickerView()) {
          @case ('menu') {
          <button type="button" class="rail-popover-item" (click)="choosePhotoFromDevice()">
            <i class="fa fa-desktop w-4 text-center"></i> From device
          </button>
          <button type="button" class="rail-popover-item" (click)="choosePhotoFromAlbums()">
            <i class="fa fa-images w-4 text-center"></i> From albums
          </button>
          }
          @case ('albums') {
          <div class="rail-popover-header">
            <button type="button" (click)="backToPhotoMenu()"><i class="fa fa-chevron-left"></i></button>
            <span>Albums</span>
          </div>
          @if (albumsLoading()) {
          <div class="flex items-center justify-center py-6 text-gray-400 text-xs"><i
              class="fa fa-spinner fa-spin"></i></div>
          } @else if (albums().length === 0) {
          <p class="text-[11px] text-gray-500 px-2 py-3 text-center">No albums yet.</p>
          } @else {
          <div class="max-h-64 overflow-y-auto">
            @for (album of albums(); track album.id) {
            <div class="album-list-item" (click)="selectAlbum(album)">
              @if (album.albumCoverUrl) {
              <img [src]="album.albumCoverUrl" class="album-cover" alt="" />
              } @else {
              <div class="album-cover flex items-center justify-center text-gray-400"><i
                  class="fa fa-images text-xs"></i></div>
              }
              <span class="truncate">{{ album.albumName }}</span>
            </div>
            }
          </div>
          }
          }
          @case ('album-images') {
          <div class="rail-popover-header">
            <button type="button" (click)="backToAlbumList()"><i class="fa fa-chevron-left"></i></button>
            <span class="truncate">{{ selectedAlbum()?.albumName }}</span>
          </div>
          @if (albumImagesLoading()) {
          <div class="flex items-center justify-center py-6 text-gray-400 text-xs"><i
              class="fa fa-spinner fa-spin"></i></div>
          } @else if (albumImages().length === 0) {
          <p class="text-[11px] text-gray-500 px-2 py-3 text-center">No images in this album.</p>
          } @else {
          <div class="photo-grid">
            @for (image of albumImages(); track image.id) {
            <button type="button" class="photo-grid-item" (click)="pickAlbumImage(image)">
              <img [src]="image.variants?.mobile || image.variants?.laptop" alt="" />
            </button>
            }
          </div>
          }
          }
          }
        </div>
        }
      </div>
      }

      @if (isAllowed('text')) {
      <button type="button" class="rail-btn" (click)="addTextLayer()" title="Add text">
        <i class="fa fa-font"></i><span>Text</span>
      </button>
      }
      @if (isAllowed('decoration')) {
      <div class="rail-item">
        <button type="button" class="rail-btn" [class.active]="showShapeMenu()" (click)="toggleShapeMenu()"
          title="Add shape">
          <i class="fa fa-shapes"></i><span>Shape</span>
        </button>

        <!-- Shape flyout — icon-only grid, hover an icon for its name -->
        @if (showShapeMenu()) {
        <div class="rail-popover">
          <div class="shape-grid">
            @for (shape of shapeChoices; track shape.type) {
            <button type="button" class="shape-grid-item icon-btn" [attr.data-tip]="shape.label"
              (click)="addShapeLayer(shape.type)">
              <i [class]="shape.icon"></i>
            </button>
            }
          </div>
        </div>
        }
      </div>
      }
      @if (isAllowed('qr')) {
      <button type="button" class="rail-btn" (click)="openQrPrompt()" title="Add QR code">
        <i class="fa fa-qrcode"></i><span>QR</span>
      </button>
      }
    </div>

    <!-- Hidden file inputs driven by the rail buttons above -->
    <input #photoFileInput type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" class="hidden"
      (change)="onPhotoFileSelected($event)" />
    <input #replaceFileInput type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
      class="hidden" (change)="onReplaceFileSelected($event)" />
    <input #importFileInput type="file" accept=".json,application/json" class="hidden"
      (change)="onImportFileSelected($event)" />

    <!-- ARTBOARD — zoomable/pannable canvas viewport (Ctrl/Cmd + scroll to zoom) -->
    <div class="artboard-viewport" #viewport (wheel)="onWheel($event)">
      <div class="artboard-wrapper" [style.width.px]="artboardSize.width" [style.height.px]="artboardSize.height"
        [style.transform]="'scale(' + zoom() + ')'">
        <canvas #fabricCanvas></canvas>
        @if (gridEnabled()) {
        <div class="artboard-grid-overlay" [style.--grid-size.px]="gridSize()"></div>
        }
        @if (!activeLayer()) {
        <!-- Artboard resize handles — only shown with nothing selected, so they never compete visually with Fabric's own per-object resize handles. Anchored top-left (see startArtboardResize doc comment). -->
        <div class="resize-handle resize-e" (pointerdown)="startArtboardResize($event, 'e')"></div>
        <div class="resize-handle resize-s" (pointerdown)="startArtboardResize($event, 's')"></div>
        <div class="resize-handle resize-se" (pointerdown)="startArtboardResize($event, 'se')"></div>
        }
      </div>
      @if (isBusy()) {
      <div
        class="absolute inset-0 flex items-center justify-center bg-[rgba(20,22,30,0.25)] backdrop-blur-[1px] z-[5]">
        <i class="fa fa-spinner fa-spin text-white text-2xl"></i>
      </div>
      }
    </div>

    <!-- RIGHT ATTRIBUTES PANEL — properties of the currently selected layer, with a Layers panel pinned to the bottom -->
    <div class="attributes-panel">
    <div class="flex-1 min-h-0 overflow-y-auto p-3.5 no-scrollbar">
      @let layer = activeLayer();
      @if (selectionCount() > 1) {
      <div class="text-center py-6 mb-2">
        <i class="fa fa-object-group text-gray-400 text-xl mb-2"></i>
        <p class="text-xs text-gray-300 font-semibold mb-1">{{ selectionCount() }} objects selected</p>
        <p class="text-[11px] text-gray-500 leading-relaxed mb-3">
          Group them to move, style, or reorder them together as one layer.
        </p>
        <button type="button" (click)="groupSelectedLayers()"
          class="w-full flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white transition-all">
          <i class="fa fa-object-group"></i> Group selection
        </button>
      </div>
      } @else if (!layer) {
      <div class="text-center py-8 mb-2">
        <i class="fa fa-arrow-pointer text-gray-500 text-xl mb-2"></i>
        <p class="text-xs text-gray-400 leading-relaxed">
          Select a layer on the canvas, or add one from the left, to edit its properties here.
        </p>
      </div>

      <div class="attr-section-title" style="border-top: none; padding-top: 0;">Artboard size</div>
      <div class="attr-section">
        <label class="attr-label">Width &times; Height (px)</label>
        <div class="attr-row">
          <input type="number" class="attr-input" min="100" max="4000" [ngModel]="round(artboardSize.width)"
            (ngModelChange)="setArtboardWidth(+$event)" placeholder="W" />
          <input type="number" class="attr-input" min="100" max="4000" [ngModel]="round(artboardSize.height)"
            (ngModelChange)="setArtboardHeight(+$event)" placeholder="H" />
        </div>
        <p class="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
          Or drag the handles on the artboard's right, bottom, or corner edge.
        </p>
      </div>
      } @else {

      <div class="flex items-center justify-between mb-3 pb-2 border-b border-white/10">
        <span class="text-xs font-bold text-gray-100 capitalize flex items-center gap-2">
          <i [class]="layerIcon(layer.type, isGroupLayer(layer))" class="header-type-icon"></i>
          {{ layerLabel(layer.type, isGroupLayer(layer)) }}
        </span>
        <button type="button" (click)="deleteActive()" title="Delete layer" class="header-delete-btn">
          <i class="fa fa-trash"></i>
        </button>
      </div>

      <!-- GROUP-specific: ungroup, and nothing else (a Group wrapper has no fill/border of its own — expand it to edit the objects inside individually). -->
      @if (isGroupLayer(layer)) {
      <div class="attr-section">
        <p class="text-[11px] text-gray-500 leading-relaxed mb-2">
          A group of {{ layer.children?.length || 0 }} objects. Ungroup to select and edit them individually again.
        </p>
        <button type="button" (click)="ungroupActiveLayer()"
          class="w-full flex items-center justify-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg text-gray-200 bg-white/10 hover:bg-white/15 transition-all">
          <i class="fa fa-object-ungroup"></i> Ungroup
        </button>
      </div>
      }

      <!-- TEXT-specific attributes -->
      @if (layer.type === 'text') {
      <div class="attr-section">
        <label class="attr-label">Text</label>
        <textarea class="attr-input" rows="2" [ngModel]="layer.props['text']"
          (ngModelChange)="updateActive({text: $event})"></textarea>
      </div>
      <div class="attr-section">
        <label class="attr-label">Font</label>
        <select class="attr-input" [ngModel]="layer.props['fontFamily']"
          (ngModelChange)="updateActive({fontFamily: $event})">
          @for (f of fontChoices; track f) {
          <option [value]="f">{{ f }}</option>
          }
        </select>
      </div>
      <div class="attr-section">
        <label class="attr-label">Size</label>
        <input type="number" class="attr-input" [ngModel]="layer.props['fontSize']"
          (ngModelChange)="updateActive({fontSize: +$event})" />
      </div>
      <div class="attr-section">
        <label class="attr-label">Color</label>
        <div class="attr-toggle-group mb-2">
          <button type="button" [class.active]="(layer.props['fillMode'] || 'solid') === 'solid'"
            (click)="setFillMode('solid')">Solid</button>
          <button type="button" [class.active]="layer.props['fillMode'] === 'brush'"
            (click)="setFillMode('brush')">Brush</button>
        </div>
        @if (layer.props['fillMode'] === 'brush') {
        <select class="attr-input mb-2" [ngModel]="currentBrushStyle(layer)"
          (ngModelChange)="setBrushStyle($event)">
          @for (b of brushStyles; track b.id) {
          <option [value]="b.id">{{ b.label }}</option>
          }
        </select>
        }
        <div class="attr-row color-field-row">
          <input type="color" class="color-swatch" [ngModel]="layer.props['color']"
            (ngModelChange)="updateActive({color: $event})" />
          <input type="text" class="attr-input" [ngModel]="layer.props['color']"
            (ngModelChange)="updateActive({color: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickTextColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
        </div>
      </div>
      <div class="attr-section">
        <label class="attr-label">Align</label>
        <div class="attr-toggle-group">
          <button type="button" class="icon-btn" data-tip="Align left" [class.active]="layer.props['align'] === 'left'"
            (click)="updateActive({align: 'left'})"><i class="fa fa-align-left"></i></button>
          <button type="button" class="icon-btn" data-tip="Align center"
            [class.active]="layer.props['align'] === 'center'" (click)="updateActive({align: 'center'})"><i
              class="fa fa-align-center"></i></button>
          <button type="button" class="icon-btn" data-tip="Align right"
            [class.active]="layer.props['align'] === 'right'" (click)="updateActive({align: 'right'})"><i
              class="fa fa-align-right"></i></button>
        </div>
      </div>
      <div class="attr-section">
        <label class="attr-label">Style</label>
        <div class="attr-toggle-group">
          <button type="button" class="icon-btn" data-tip="Bold" [class.active]="layer.props['bold']"
            (click)="updateActive({bold: !layer.props['bold']})"><i class="fa fa-bold"></i></button>
          <button type="button" class="icon-btn" data-tip="Italic" [class.active]="layer.props['italic']"
            (click)="updateActive({italic: !layer.props['italic']})"><i class="fa fa-italic"></i></button>
          <button type="button" class="icon-btn" data-tip="Underline" [class.active]="layer.props['underline']"
            (click)="updateActive({underline: !layer.props['underline']})"><i class="fa fa-underline"></i></button>
          <button type="button" class="icon-btn" data-tip="Strikethrough" [class.active]="layer.props['linethrough']"
            (click)="updateActive({linethrough: !layer.props['linethrough']})"><i
              class="fa fa-strikethrough"></i></button>
        </div>
      </div>

      <div class="attr-section">
        <div class="attr-row">
          <i class="fa fa-text-width text-gray-400 w-4 text-center icon-btn" data-tip="Letter spacing"></i>
          <input type="range" class="attr-slider" min="-100" max="600" [ngModel]="layer.props['letterSpacing'] || 0"
            (ngModelChange)="updateActive({letterSpacing: +$event})" />
        </div>
      </div>
      <div class="attr-section">
        <div class="attr-row">
          <i class="fa fa-text-height text-gray-400 w-4 text-center icon-btn" data-tip="Line height"></i>
          <input type="range" class="attr-slider" min="0.8" max="2.5" step="0.05"
            [ngModel]="layer.props['lineHeight'] || 1.16" (ngModelChange)="updateActive({lineHeight: +$event})" />
        </div>
      </div>

      <div class="attr-section">
        <label class="attr-label">Curve</label>
        <input type="range" class="attr-slider" min="-100" max="100" [ngModel]="layer.props['curve'] || 0"
          (ngModelChange)="updateActive({curve: +$event})" />
        <div class="flex justify-between text-[10px] text-gray-500 mt-0.5">
          <span>Bend down</span>
          <span>Straight</span>
          <span>Bend up</span>
        </div>
      </div>

      <div class="attr-section-title">Background</div>
      <div class="attr-section">
        <label class="attr-label">Highlight behind text</label>
        <div class="attr-row color-field-row" [class.is-off]="currentOpacity(layer, 'background') <= 0">
          <input type="color" class="color-swatch" [ngModel]="layer.props['backgroundColor'] || '#ffe27a'"
            [disabled]="currentOpacity(layer, 'background') <= 0"
            (ngModelChange)="updateActive({backgroundColor: $event})" />
          <input type="text" class="attr-input"
            [ngModel]="currentOpacity(layer, 'background') > 0 ? (layer.props['backgroundColor'] || '#ffe27a') : 'Off'"
            [disabled]="currentOpacity(layer, 'background') <= 0"
            (ngModelChange)="updateActive({backgroundColor: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickTextBackgroundColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <div class="relative">
            <button type="button" class="icon-btn color-toggle-btn"
              [class.is-active]="currentOpacity(layer, 'background') < 100" data-tip="Transparency"
              (click)="toggleOpacityPopup('background')">
              <i class="fa fa-sliders"></i>
            </button>
            @if (showOpacityPopupFor() === 'background') {
            <div class="rail-popover !left-auto !right-0 !top-full !ml-0 !mt-2 w-44">
              <label class="attr-label">Transparency</label>
              <input type="range" class="attr-slider" min="0" max="100" [ngModel]="currentOpacity(layer, 'background')"
                (ngModelChange)="setOpacity('background', +$event)" />
              <div class="text-[10px] text-gray-400 text-right mt-0.5">{{ currentOpacity(layer, 'background') }}%</div>
            </div>
            }
          </div>
        </div>
      </div>

      <div class="attr-section-title">Shadow</div>
      <div class="attr-section">
        <label class="attr-label">Shadow color</label>
        <div class="attr-row color-field-row" [class.is-off]="currentOpacity(layer, 'shadow') <= 0">
          <input type="color" class="color-swatch" [ngModel]="layer.props['shadowColor'] || '#000000'"
            [disabled]="currentOpacity(layer, 'shadow') <= 0" (ngModelChange)="updateActive({shadowColor: $event})" />
          <input type="text" class="attr-input"
            [ngModel]="currentOpacity(layer, 'shadow') > 0 ? (layer.props['shadowColor'] || '#000000') : 'Off'"
            [disabled]="currentOpacity(layer, 'shadow') <= 0" (ngModelChange)="updateActive({shadowColor: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickShadowColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <div class="relative">
            <button type="button" class="icon-btn color-toggle-btn"
              [class.is-active]="currentOpacity(layer, 'shadow') < 100" data-tip="Transparency"
              (click)="toggleOpacityPopup('shadow')">
              <i class="fa fa-sliders"></i>
            </button>
            @if (showOpacityPopupFor() === 'shadow') {
            <div class="rail-popover !left-auto !right-0 !top-full !ml-0 !mt-2 w-44">
              <label class="attr-label">Transparency</label>
              <input type="range" class="attr-slider" min="0" max="100" [ngModel]="currentOpacity(layer, 'shadow')"
                (ngModelChange)="setOpacity('shadow', +$event)" />
              <div class="text-[10px] text-gray-400 text-right mt-0.5">{{ currentOpacity(layer, 'shadow') }}%</div>
            </div>
            }
          </div>
        </div>
      </div>
      @if (currentOpacity(layer, 'shadow') > 0) {
      <div class="attr-section">
        <label class="attr-label">Blur</label>
        <input type="range" class="attr-slider" min="0" max="40" [ngModel]="layer.props['shadowBlur']"
          (ngModelChange)="updateActive({shadowBlur: +$event})" />
      </div>
      }

      <div class="attr-section-title">Outline</div>
      <div class="attr-section">
        <label class="attr-label">Border color</label>
        <div class="attr-row color-field-row" [class.is-off]="currentOpacity(layer, 'textStroke') <= 0">
          <input type="color" class="color-swatch" [ngModel]="layer.props['textStrokeColor'] || '#000000'"
            [disabled]="currentOpacity(layer, 'textStroke') <= 0"
            (ngModelChange)="updateActive({textStrokeColor: $event})" />
          <input type="text" class="attr-input"
            [ngModel]="currentOpacity(layer, 'textStroke') > 0 ? (layer.props['textStrokeColor'] || '#000000') : 'Off'"
            [disabled]="currentOpacity(layer, 'textStroke') <= 0"
            (ngModelChange)="updateActive({textStrokeColor: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickTextStrokeColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <div class="relative">
            <button type="button" class="icon-btn color-toggle-btn"
              [class.is-active]="currentOpacity(layer, 'textStroke') < 100" data-tip="Transparency"
              (click)="toggleOpacityPopup('textStroke')">
              <i class="fa fa-sliders"></i>
            </button>
            @if (showOpacityPopupFor() === 'textStroke') {
            <div class="rail-popover !left-auto !right-0 !top-full !ml-0 !mt-2 w-44">
              <label class="attr-label">Transparency</label>
              <input type="range" class="attr-slider" min="0" max="100" [ngModel]="currentOpacity(layer, 'textStroke')"
                (ngModelChange)="setOpacity('textStroke', +$event)" />
              <div class="text-[10px] text-gray-400 text-right mt-0.5">{{ currentOpacity(layer, 'textStroke') }}%</div>
            </div>
            }
          </div>
        </div>
      </div>
      @if (currentOpacity(layer, 'textStroke') > 0) {
      <div class="attr-section">
        <label class="attr-label">Border width</label>
        <input type="range" class="attr-slider" min="1" max="10" [ngModel]="layer.props['textStrokeWidth'] || 2"
          (ngModelChange)="updateActive({textStrokeWidth: +$event})" />
      </div>
      }
      }

      <!-- SHAPE-specific attributes (not a Group — that has its own Ungroup-only section above) -->
      @if (layer.type === 'decoration' && !isGroupLayer(layer)) {
      <div class="attr-section-title" style="border-top: none; padding-top: 0;">Fill</div>
      <div class="attr-section">
        <div class="attr-toggle-group">
          <button type="button" [class.active]="(layer.props['fillMode'] || 'solid') === 'solid'"
            (click)="setFillMode('solid')">Solid</button>
          <button type="button" [class.active]="layer.props['fillMode'] === 'gradient'"
            (click)="setFillMode('gradient')">Gradient</button>
          <button type="button" [class.active]="layer.props['fillMode'] === 'brush'"
            (click)="setFillMode('brush')">Brush</button>
        </div>
      </div>
      @if (layer.props['fillMode'] === 'brush') {
      <div class="attr-section">
        <select class="attr-input mb-2" [ngModel]="currentBrushStyle(layer)"
          (ngModelChange)="setBrushStyle($event)">
          @for (b of brushStyles; track b.id) {
          <option [value]="b.id">{{ b.label }}</option>
          }
        </select>
        <div class="attr-row color-field-row">
          <input type="color" class="color-swatch" [ngModel]="layer.props['fill'] || '#2781ff'"
            (ngModelChange)="updateActive({fill: $event})" />
          <input type="text" class="attr-input" [ngModel]="layer.props['fill'] || '#2781ff'"
            (ngModelChange)="updateActive({fill: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickFillColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
        </div>
      </div>
      } @else if (layer.props['fillMode'] === 'gradient') {
      <div class="attr-section">
        <div class="gradient-stops">
          @for (stop of (layer.props['fillGradientStops'] || defaultGradientStops); track $index; let i = $index) {
          <div class="gradient-stop-row">
            <input type="color" class="color-swatch sm" [ngModel]="stop.color"
              (ngModelChange)="updateGradientStop('fillGradientStops', i, {color: $event})" />
            <input type="text" class="attr-input" [ngModel]="stop.color"
              (ngModelChange)="updateGradientStop('fillGradientStops', i, {color: $event})" />
            <input type="number" class="attr-input xs" min="0" max="100" [ngModel]="stop.offset"
              (ngModelChange)="updateGradientStop('fillGradientStops', i, {offset: +$event})" />
            <button type="button" class="icon-btn" data-tip="Remove stop"
              [disabled]="(layer.props['fillGradientStops'] || defaultGradientStops).length <= 2"
              (click)="removeGradientStop('fillGradientStops', i)">
              <i class="fa fa-xmark"></i>
            </button>
          </div>
          }
          <button type="button" class="add-stop-btn" (click)="addGradientStop('fillGradientStops')">
            <i class="fa fa-plus"></i> Add color stop
          </button>
        </div>
      </div>
      } @else {
      <div class="attr-section">
        <div class="attr-row color-field-row" [class.is-off]="currentOpacity(layer, 'fill') <= 0">
          <input type="color" class="color-swatch" [ngModel]="layer.props['fill'] || '#2781ff'"
            [disabled]="currentOpacity(layer, 'fill') <= 0" (ngModelChange)="updateActive({fill: $event})" />
          <input type="text" class="attr-input"
            [ngModel]="currentOpacity(layer, 'fill') > 0 ? (layer.props['fill'] || '#2781ff') : 'Off'"
            [disabled]="currentOpacity(layer, 'fill') <= 0" (ngModelChange)="updateActive({fill: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickFillColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <div class="relative">
            <button type="button" class="icon-btn color-toggle-btn"
              [class.is-active]="currentOpacity(layer, 'fill') < 100" data-tip="Transparency"
              (click)="toggleOpacityPopup('fill')">
              <i class="fa fa-sliders"></i>
            </button>
            @if (showOpacityPopupFor() === 'fill') {
            <div class="rail-popover !left-auto !right-0 !top-full !ml-0 !mt-2 w-44">
              <label class="attr-label">Transparency</label>
              <input type="range" class="attr-slider" min="0" max="100" [ngModel]="currentOpacity(layer, 'fill')"
                (ngModelChange)="setOpacity('fill', +$event)" />
              <div class="text-[10px] text-gray-400 text-right mt-0.5">{{ currentOpacity(layer, 'fill') }}%</div>
            </div>
            }
          </div>
        </div>
      </div>
      }

      <div class="attr-section-title">Border</div>
      <div class="attr-section">
        <div class="attr-toggle-group">
          <button type="button" [class.active]="strokeStyleOf(layer) === 'none'"
            (click)="setStrokeStyle('none')">None</button>
          <button type="button" [class.active]="strokeStyleOf(layer) === 'solid'"
            (click)="setStrokeStyle('solid')">Solid</button>
          <button type="button" [class.active]="strokeStyleOf(layer) === 'dashed'"
            (click)="setStrokeStyle('dashed')">Dashed</button>
          <button type="button" [class.active]="strokeStyleOf(layer) === 'dotted'"
            (click)="setStrokeStyle('dotted')">Dotted</button>
        </div>
      </div>
      @if (strokeStyleOf(layer) !== 'none') {
      <div class="attr-section">
        <div class="attr-toggle-group">
          <button type="button" [class.active]="(layer.props['strokeMode'] || 'solid') === 'solid'"
            (click)="setStrokeMode('solid')">Solid color</button>
          <button type="button" [class.active]="layer.props['strokeMode'] === 'gradient'"
            (click)="setStrokeMode('gradient')">Gradient</button>
          <button type="button" [class.active]="layer.props['strokeMode'] === 'brush'"
            (click)="setStrokeMode('brush')">Brush</button>
        </div>
      </div>
      @if (layer.props['strokeMode'] === 'gradient') {
      <div class="attr-section">
        <div class="gradient-stops">
          @for (stop of (layer.props['strokeGradientStops'] || defaultGradientStops); track $index; let i = $index) {
          <div class="gradient-stop-row">
            <input type="color" class="color-swatch sm" [ngModel]="stop.color"
              (ngModelChange)="updateGradientStop('strokeGradientStops', i, {color: $event})" />
            <input type="text" class="attr-input" [ngModel]="stop.color"
              (ngModelChange)="updateGradientStop('strokeGradientStops', i, {color: $event})" />
            <input type="number" class="attr-input xs" min="0" max="100" [ngModel]="stop.offset"
              (ngModelChange)="updateGradientStop('strokeGradientStops', i, {offset: +$event})" />
            <button type="button" class="icon-btn" data-tip="Remove stop"
              [disabled]="(layer.props['strokeGradientStops'] || defaultGradientStops).length <= 2"
              (click)="removeGradientStop('strokeGradientStops', i)">
              <i class="fa fa-xmark"></i>
            </button>
          </div>
          }
          <button type="button" class="add-stop-btn" (click)="addGradientStop('strokeGradientStops')">
            <i class="fa fa-plus"></i> Add color stop
          </button>
        </div>
      </div>
      } @else if (layer.props['strokeMode'] === 'brush') {
      <div class="attr-section">
        <select class="attr-input mb-2" [ngModel]="currentStrokeBrushStyle(layer)"
          (ngModelChange)="setStrokeBrushStyle($event)">
          @for (b of brushStyles; track b.id) {
          <option [value]="b.id">{{ b.label }}</option>
          }
        </select>
        <div class="attr-row color-field-row">
          <input type="color" class="color-swatch" [ngModel]="layer.props['stroke'] || '#000000'"
            (ngModelChange)="updateActive({stroke: $event})" />
          <input type="text" class="attr-input" [ngModel]="layer.props['stroke'] || '#000000'"
            (ngModelChange)="updateActive({stroke: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickStrokeColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Hide border"
            (click)="setStrokeStyle('none')">
            <i class="fa fa-eye"></i>
          </button>
        </div>
      </div>
      } @else {
      <div class="attr-section">
        <div class="attr-row color-field-row">
          <input type="color" class="color-swatch" [ngModel]="layer.props['stroke'] || '#000000'"
            (ngModelChange)="updateActive({stroke: $event})" />
          <input type="text" class="attr-input" [ngModel]="layer.props['stroke'] || '#000000'"
            (ngModelChange)="updateActive({stroke: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickStrokeColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Hide border"
            (click)="setStrokeStyle('none')">
            <i class="fa fa-eye"></i>
          </button>
        </div>
      </div>
      }
      <div class="attr-section">
        <label class="attr-label">Border width</label>
        <input type="number" class="attr-input" min="1" [ngModel]="layer.props['strokeWidth'] || 4"
          (ngModelChange)="updateActive({strokeWidth: +$event})" />
      </div>
      }

      <div class="attr-section-title">Shadow</div>
      <div class="attr-section">
        <label class="attr-label">Shadow color</label>
        <div class="attr-row color-field-row" [class.is-off]="currentOpacity(layer, 'shadow') <= 0">
          <input type="color" class="color-swatch" [ngModel]="layer.props['shadowColor'] || '#000000'"
            [disabled]="currentOpacity(layer, 'shadow') <= 0" (ngModelChange)="updateActive({shadowColor: $event})" />
          <input type="text" class="attr-input"
            [ngModel]="currentOpacity(layer, 'shadow') > 0 ? (layer.props['shadowColor'] || '#000000') : 'Off'"
            [disabled]="currentOpacity(layer, 'shadow') <= 0" (ngModelChange)="updateActive({shadowColor: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickShadowColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <div class="relative">
            <button type="button" class="icon-btn color-toggle-btn"
              [class.is-active]="currentOpacity(layer, 'shadow') < 100" data-tip="Transparency"
              (click)="toggleOpacityPopup('shadow')">
              <i class="fa fa-sliders"></i>
            </button>
            @if (showOpacityPopupFor() === 'shadow') {
            <div class="rail-popover !left-auto !right-0 !top-full !ml-0 !mt-2 w-44">
              <label class="attr-label">Transparency</label>
              <input type="range" class="attr-slider" min="0" max="100" [ngModel]="currentOpacity(layer, 'shadow')"
                (ngModelChange)="setOpacity('shadow', +$event)" />
              <div class="text-[10px] text-gray-400 text-right mt-0.5">{{ currentOpacity(layer, 'shadow') }}%</div>
            </div>
            }
          </div>
        </div>
      </div>
      @if (currentOpacity(layer, 'shadow') > 0) {
      <div class="attr-section">
        <label class="attr-label">Blur</label>
        <input type="range" class="attr-slider" min="0" max="40" [ngModel]="layer.props['shadowBlur']"
          (ngModelChange)="updateActive({shadowBlur: +$event})" />
      </div>
      }

      <div class="attr-section-title">Blur</div>
      <div class="attr-section">
        <div class="attr-toggle-group">
          <button type="button" [class.active]="(layer.props['blurType'] || 'none') === 'none'"
            (click)="setShapeBlurType('none')">None</button>
          <button type="button" [class.active]="layer.props['blurType'] === 'linear'"
            (click)="setShapeBlurType('linear')">Linear</button>
          <button type="button" [class.active]="layer.props['blurType'] === 'radial'"
            (click)="setShapeBlurType('radial')">Radial</button>
        </div>
      </div>
      @if (layer.props['blurType'] === 'linear' || layer.props['blurType'] === 'radial') {
      <div class="attr-section">
        <label class="attr-label">Amount</label>
        <input type="range" class="attr-slider" min="1" max="30" [ngModel]="layer.props['blurAmount'] || 8"
          (ngModelChange)="updateActive({blurAmount: +$event})" />
      </div>
      }
      }

      <!-- QR-specific attributes -->
      @if (layer.type === 'qr') {
      <div class="attr-section">
        <label class="attr-label">Target URL</label>
        <input type="text" class="attr-input" [ngModel]="layer.props['targetUrl']"
          (ngModelChange)="updateQrTargetUrl($event)" placeholder="https://..." />
      </div>
      }

      <!-- Photo-like layers (background / photo / logo) -->
      @if (layer.type === 'background-image' || layer.type === 'photo' || layer.type === 'logo') {
      <div class="attr-section">
        <button type="button" (click)="triggerReplaceSelectedImage()"
          class="w-full text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-gray-100 transition-all flex items-center justify-center gap-1.5">
          <i class="fa fa-arrows-rotate"></i> Replace image
        </button>
      </div>

      <div class="attr-section-title" style="border-top: none; padding-top: 0;">Shadow</div>
      <div class="attr-section">
        <label class="attr-label">Shadow color</label>
        <div class="attr-row color-field-row" [class.is-off]="currentOpacity(layer, 'shadow') <= 0">
          <input type="color" class="color-swatch" [ngModel]="layer.props['shadowColor'] || '#000000'"
            [disabled]="currentOpacity(layer, 'shadow') <= 0" (ngModelChange)="updateActive({shadowColor: $event})" />
          <input type="text" class="attr-input"
            [ngModel]="currentOpacity(layer, 'shadow') > 0 ? (layer.props['shadowColor'] || '#000000') : 'Off'"
            [disabled]="currentOpacity(layer, 'shadow') <= 0" (ngModelChange)="updateActive({shadowColor: $event})" />
          @if (eyeDropperSupported) {
          <button type="button" class="icon-btn color-toggle-btn" data-tip="Pick from artboard"
            (click)="pickShadowColor()">
            <i class="fa fa-eye-dropper"></i>
          </button>
          }
          <div class="relative">
            <button type="button" class="icon-btn color-toggle-btn"
              [class.is-active]="currentOpacity(layer, 'shadow') < 100" data-tip="Transparency"
              (click)="toggleOpacityPopup('shadow')">
              <i class="fa fa-sliders"></i>
            </button>
            @if (showOpacityPopupFor() === 'shadow') {
            <div class="rail-popover !left-auto !right-0 !top-full !ml-0 !mt-2 w-44">
              <label class="attr-label">Transparency</label>
              <input type="range" class="attr-slider" min="0" max="100" [ngModel]="currentOpacity(layer, 'shadow')"
                (ngModelChange)="setOpacity('shadow', +$event)" />
              <div class="text-[10px] text-gray-400 text-right mt-0.5">{{ currentOpacity(layer, 'shadow') }}%</div>
            </div>
            }
          </div>
        </div>
      </div>
      }

      <!-- Common: position / rotation (background always fills the artboard, so these don't apply to it) -->
      @if (layer.type !== 'background-image') {
      <div class="attr-section-title" style="border-top: none; padding-top: 0;">Transform</div>
      <div class="attr-section">
        <label class="attr-label">Position</label>
        <div class="attr-row">
          <input type="number" class="attr-input" [ngModel]="round(layer.x)"
            (ngModelChange)="updateActive({x: +$event})" placeholder="X" />
          <input type="number" class="attr-input" [ngModel]="round(layer.y)"
            (ngModelChange)="updateActive({y: +$event})" placeholder="Y" />
        </div>
      </div>
      <div class="attr-section">
        <label class="attr-label">Rotation</label>
        <input type="range" class="attr-slider" min="0" max="360" [ngModel]="layer.rotation"
          (ngModelChange)="updateActive({rotation: +$event})" />
      </div>
      }
      }
    </div>

    <!-- LAYERS PANEL — separated by a divider, pinned to the bottom, visible
         regardless of selection. Topmost row = frontmost object. Click a row
         to select it on the canvas; the chevrons reorder without needing to
         select first. -->
    <div class="layer-order-panel">
      <div class="layer-order-header">
        <span>Layers</span>
        <span class="layer-count">{{ layers().length }}</span>
      </div>
      <div class="layer-order-list no-scrollbar">
        @if (layers().length === 0) {
        <p class="text-[11px] text-gray-500 px-2 py-3 text-center">No layers yet.</p>
        }
        @for (l of layers(); track l.id; let i = $index) {
        @let rowActive = activeLayer()?.id === l.id;
        <div class="layer-order-row"
          [class]="rowActive ? '!bg-secondary-600 !text-white shadow-[inset_3px_0_0_0_theme(colors.secondary.300)]' : ''"
          (click)="selectLayerRow(l.id)">
          <i [class]="layerIcon(l.type, isGroupLayer(l)) + ' layer-row-icon'"
            [class.text-secondary-200]="rowActive"></i>
          @if (editingLayerId() === l.id) {
          <input type="text" class="layer-row-rename-input" appAutofocus [(ngModel)]="editingLayerName"
            (click)="$event.stopPropagation()" (keydown.enter)="commitRenameLayer(l.id)"
            (keydown.escape)="cancelRenameLayer()" (blur)="commitRenameLayer(l.id)" />
          } @else {
          <span class="layer-row-label truncate" (dblclick)="startRenameLayer(l, $event)">{{ layerRowLabel(l)
            }}</span>
          }
          <div class="layer-row-actions">
            <button type="button" [disabled]="i === 0" (click)="reorderLayer(l.id, 'up', $event)"><i
                class="fa fa-chevron-up"></i></button>
            <button type="button" [disabled]="i === layers().length - 1"
              (click)="reorderLayer(l.id, 'down', $event)"><i class="fa fa-chevron-down"></i></button>
          </div>
        </div>
        <!-- Group children — display-only (not independently selectable/reorderable
             yet; ungroup to edit them individually), indented in a wrapper div
             rather than via a modifier class, so this never has to fight the
             base .layer-order-row's own padding-left for specificity. -->
        @for (child of (l.children || []); track child.id) {
        <div class="pl-6">
          <div class="layer-order-row !cursor-default opacity-70">
            <i [class]="layerIcon(child.type, isGroupLayer(child)) + ' layer-row-icon'"></i>
            <span class="layer-row-label truncate">{{ layerRowLabel(child) }}</span>
          </div>
        </div>
        }
        }
      </div>
    </div>
  </div>
  </div>
</div>

<!-- QR add prompt -->
@if (showQrPrompt()) {
<div class="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-xs flex items-center justify-center p-4"
  (click)="closeQrPrompt()">
  <div class="bg-white rounded-xl p-5 w-full max-w-sm shadow-2xl space-y-3" (click)="$event.stopPropagation()">
    <p class="text-sm font-bold text-gray-800 flex items-center gap-2"><i
        class="fa fa-qrcode text-primary-500"></i> Add QR code</p>
    <input type="text" [(ngModel)]="qrUrlInput" placeholder="https://..."
      class="w-full p-2.5 border border-gray-200 rounded-lg text-sm text-gray-800" (keydown.enter)="confirmAddQr()" />
    <div class="flex justify-end gap-2 pt-1">
      <button type="button" (click)="closeQrPrompt()"
        class="text-xs font-semibold px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
      <button type="button" (click)="confirmAddQr()" [disabled]="!qrUrlInput.trim()"
        class="text-xs font-semibold px-3.5 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white">Add</button>
    </div>
  </div>
</div>
}
